import { create } from 'zustand';
import type { TerritoryBlockReason } from '@/lib/territory/types';

/**
 * Drives the territory block screen. Unlike the KYC gate, a territory block
 * can't self-resolve in-session, so there's no replay/await — the gate just
 * surfaces the block screen and the original 403 propagates. Opened by the 403
 * interceptor (server is authoritative) and proactively by the gated entry
 * points (deposit / trade / SU-completion) before they fire.
 */
interface TerritoryGateState {
  open: boolean;
  reason: TerritoryBlockReason | null;
  /** Set when the block is category-scoped (CATEGORY_BLOCKED). */
  category: string | null;
  openGate: (payload?: { reason?: TerritoryBlockReason | null; category?: string | null }) => void;
  close: () => void;
}

const useTerritoryGateStore = create<TerritoryGateState>((set) => ({
  open: false,
  reason: null,
  category: null,
  openGate: (payload) => set({ open: true, reason: payload?.reason ?? null, category: payload?.category ?? null }),
  close: () => set({ open: false, reason: null, category: null }),
}));

export const useTerritoryGateOpen = () => useTerritoryGateStore((s) => s.open);
export const useTerritoryGateReason = () => useTerritoryGateStore((s) => s.reason);
export const useTerritoryGateCategory = () => useTerritoryGateStore((s) => s.category);
export const useOpenTerritoryGate = () => useTerritoryGateStore((s) => s.openGate);
export const useCloseTerritoryGate = () => useTerritoryGateStore((s) => s.close);

/** Non-hook accessors for the 403 interceptor (outside React). */
export const openTerritoryGateImperative = (payload?: { reason?: TerritoryBlockReason | null; category?: string | null }) =>
  useTerritoryGateStore.getState().openGate(payload);
export const closeTerritoryGateImperative = () => useTerritoryGateStore.getState().close();
